import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { components } from '@/api/generated/schema';

export type JobVisit = components['schemas']['JobVisitResponse'];

/** Where a visit goes: when, and who is booked on it. A TOTAL statement, like the API's. */
export interface VisitPlacement {
  scheduledStart: string;
  scheduledEnd: string | null;
  assignedUserId: string | null;
}

/**
 * The API declined a visit change and said why (M2-52): finished work, the
 * last visit of a job someone is driving to, or an exact duplicate. Carried
 * up verbatim, like `JobStatusRefusedError`, because the sentence is the only
 * part a dispatcher can act on.
 */
export class VisitRefusedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'VisitRefusedError';
  }
}

/**
 * A 404: the visit is gone (someone else removed it), or the technician can no
 * longer be booked (deactivated since). Told apart from a transport failure
 * because "try again" is the one thing that can never work for it — the
 * screen has to send the dispatcher back to the job, or to someone else.
 */
export class VisitGoneError extends Error {
  constructor(what: string) {
    super(`${what} found nothing: 404`);
    this.name = 'VisitGoneError';
  }
}

/**
 * A 409 is a stated refusal, read off `error` (openapi-fetch never puts a
 * non-2xx body on `data`); a 404 is `VisitGoneError`; anything else that is
 * not a success is a failure.
 */
function refusalOrFailure(error: unknown, response: Response, what: string): Error {
  if (response.status === 409) {
    return new VisitRefusedError(typeof error === 'string' ? error : `${what} refused: ${response.status}`);
  }
  if (response.status === 404) {
    return new VisitGoneError(what);
  }
  return new Error(`${what} failed: ${response.status}`);
}

/**
 * Adding, moving and removing one visit (M2-52, `JobVisitEndpoints`).
 *
 * Each invalidates EVERY job query, not just this job's: a visit changes the
 * job's span, its status (the first and last visit) and, on a one-visit job,
 * its lead — all of which the job list and the dispatch board also show.
 */
export function useJobVisitMutations(jobId: string) {
  const queryClient = useQueryClient();
  const onSuccess = () => void queryClient.invalidateQueries({ queryKey: ['jobs'] });

  const add = useMutation({
    mutationFn: async (placement: VisitPlacement) => {
      const { data, error, response } = await apiClient.POST('/api/v1/jobs/{jobId}/visits', {
        params: { path: { jobId } },
        body: placement,
      });
      if (!response.ok || !data) {
        throw refusalOrFailure(error, response, 'POST /api/v1/jobs/{jobId}/visits');
      }
      return data;
    },
    onSuccess,
  });

  const move = useMutation({
    mutationFn: async ({ visitId, placement }: { visitId: string; placement: VisitPlacement }) => {
      const { data, error, response } = await apiClient.PUT('/api/v1/jobs/{jobId}/visits/{visitId}', {
        params: { path: { jobId, visitId } },
        body: placement,
      });
      if (!response.ok || !data) {
        throw refusalOrFailure(error, response, 'PUT /api/v1/jobs/{jobId}/visits/{visitId}');
      }
      return data;
    },
    onSuccess,
  });

  const remove = useMutation({
    mutationFn: async (visitId: string) => {
      const { data, error, response } = await apiClient.DELETE('/api/v1/jobs/{jobId}/visits/{visitId}', {
        params: { path: { jobId, visitId } },
      });
      if (!response.ok || !data) {
        throw refusalOrFailure(error, response, 'DELETE /api/v1/jobs/{jobId}/visits/{visitId}');
      }
      return data;
    },
    onSuccess,
  });

  return { add, move, remove };
}
